import { useCallback, useEffect, useRef, useState } from "react";
import { ANIMATION_DURATIONS } from "../constants/animations";
import type { NewNarrativeData } from "../types/narrative";

/** The section the interstitial currently on screen is introducing. */
export interface ActiveInterstitial {
  sectionId: string;
  label: string;
  icon?: string;
}

export function useSectionTransition(
  currentSectionId: string | undefined,
  visibleSections: NewNarrativeData["sections"],
  disableInterstitials: boolean = false,
) {
  // The card and the section it introduces share one lifetime, so they share
  // one piece of state: "showing" is "having a payload". Keeping them apart
  // would allow states that cannot legally occur, and every dismissal would
  // have to unwind each field in step.
  const [interstitial, setInterstitial] = useState<ActiveInterstitial | null>(
    null,
  );
  // Not part of the card's identity: content unblocks partway through the
  // dwell, on its own timer.
  const [contentDelayed, setContentDelayed] = useState(false);

  const previousSectionIdRef = useRef<string | null>(null);
  // Sections entered at least once this page load. In-memory on purpose: a
  // reload should replay the first-time experience.
  const seenSectionIdsRef = useRef<Set<string>>(new Set());
  const interstitialTimeoutRef = useRef<number | null>(null);
  const contentDelayTimeoutRef = useRef<number | null>(null);

  const clearInterstitialTimers = useCallback(() => {
    if (interstitialTimeoutRef.current !== null) {
      clearTimeout(interstitialTimeoutRef.current);
      interstitialTimeoutRef.current = null;
    }
    if (contentDelayTimeoutRef.current !== null) {
      clearTimeout(contentDelayTimeoutRef.current);
      contentDelayTimeoutRef.current = null;
    }
  }, []);

  // Each section introduces itself exactly once: the first time the viewer
  // arrives, however they arrive — scrolled, stepped, or picked from the
  // progress menu. Every later visit is silent, and HeaderSectionLabel marks
  // the change instead. Direction is deliberately not part of the rule: gating
  // on "forward" would let a section first entered backward count as seen and
  // then never introduce itself at all.
  useEffect(() => {
    // Skip interstitial logic entirely if disabled (video mode, PDF export, etc.)
    if (disableInterstitials) return;

    const previousSectionId = previousSectionIdRef.current;
    const isFirstVisit = currentSectionId
      ? !seenSectionIdsRef.current.has(currentSectionId)
      : false;

    // Recorded before the guard below, so the section the viewer mounted on
    // (including a deep-linked ?p=) counts as seen rather than unvisited later.
    if (currentSectionId) seenSectionIdsRef.current.add(currentSectionId);
    previousSectionIdRef.current = currentSectionId || null;

    // First render, or a group change within the same section.
    if (previousSectionId === null || previousSectionId === currentSectionId) {
      return;
    }

    // Any section change invalidates a dwelling card's timers, whether this
    // change earns a new interstitial or dismisses the one on screen.
    clearInterstitialTimers();

    // Eligibility is a property of the section, not of the navigation: chrome
    // sections and sections opting out via `disableInterstitial` never get a
    // card. Resolving it to the payload itself keeps the two concerns apart at
    // the branch below.
    const section = visibleSections.find((s) => s.id === currentSectionId);
    const eligible =
      section &&
      section.id !== "intro" &&
      section.id !== "table_of_contents" &&
      section.label &&
      !section.disableInterstitial
        ? { sectionId: section.id, label: section.label, icon: section.icon }
        : null;

    if (isFirstVisit && eligible) {
      setInterstitial(eligible);
      setContentDelayed(true);

      // Let the incoming content start animating before the card clears.
      contentDelayTimeoutRef.current = window.setTimeout(() => {
        setContentDelayed(false);
      }, ANIMATION_DURATIONS.interstitial - 1000);

      interstitialTimeoutRef.current = window.setTimeout(() => {
        setInterstitial(null);
      }, ANIMATION_DURATIONS.interstitial + 500);
    } else {
      // This change earns no card, so anything still dwelling belongs to the
      // section the viewer just left — leaving it up would cover the new
      // section with the old one's title, with the header hidden behind it.
      // Reachable by retreating during a dwell: retreatGroup, unlike
      // advanceGroup, is not gated on the interstitial. Both calls below are
      // no-ops when nothing is showing.
      setInterstitial(null);
      setContentDelayed(false);
    }
  }, [
    currentSectionId,
    visibleSections,
    disableInterstitials,
    clearInterstitialTimers,
  ]);

  // Cleanup timeouts on unmount
  useEffect(() => clearInterstitialTimers, [clearInterstitialTimers]);

  return { interstitial, contentDelayed };
}
